import { Fragment, KeyboardEvent } from 'react';
import {
  Box,
  Card,
  CardHeader,
  Checkbox,
  Chip,
  Divider,
  Link,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { ComponentPrs } from '../../api/MergeOpsApi';
import { PrSummary } from '../../api/types';
import { MaintenanceModeChip } from '../../maintenance';
import { currentTagFor, stageRank } from '../../stages';
import { PrRow } from '../EntityMergeOpsContent/PrRow';

const useStyles = makeStyles(theme => ({
  card: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(42, 43, 47, 0.88)'
        : theme.palette.background.paper,
    borderColor:
      theme.palette.type === 'dark'
        ? 'rgba(196, 196, 204, 0.2)'
        : 'rgba(15, 23, 42, 0.12)',
    borderRadius: 8,
    overflow: 'hidden',
  },
  compactCard: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(42, 43, 47, 0.78)'
        : 'rgba(255, 255, 255, 0.94)',
  },
  header: {
    paddingTop: theme.spacing(1.1),
    paddingBottom: theme.spacing(1.1),
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(55, 56, 61, 0.64)'
        : 'rgba(248, 250, 252, 0.92)',
    borderBottom: `1px solid ${
      theme.palette.type === 'dark'
        ? 'rgba(196, 196, 204, 0.18)'
        : 'rgba(15, 23, 42, 0.08)'
    }`,
  },
  headerTitle: { fontWeight: 600 },
  // The whole header row is the disclosure target (not just the name), so the
  // click area matches the visible card edge. Mirrors the PrRow title toggle.
  headerClickable: {
    cursor: 'pointer',
    transition: theme.transitions.create('background-color', {
      duration: theme.transitions.duration.shortest,
    }),
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(20, 184, 166, 0.1)'
          : 'rgba(13, 148, 136, 0.06)',
    },
    '&:hover $headerTitle': {
      color: theme.palette.primary.main,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  compactHeaderTitle: {
    fontSize: '0.9rem',
  },
  phaseLabel: {
    height: 22,
    fontSize: '0.7rem',
  },
  headerTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  compactHeader: {
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
  },
  noPr: {
    padding: theme.spacing(1.25, 1.5),
    color: theme.palette.text.secondary,
    fontSize: '0.84rem',
    borderTop: `1px dashed ${
      theme.palette.type === 'dark'
        ? 'rgba(196, 196, 204, 0.2)'
        : 'rgba(15, 23, 42, 0.14)'
    }`,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(35, 36, 40, 0.46)'
        : 'rgba(248, 250, 252, 0.75)',
  },
}));

/**
 * One owned component in the group view: a header linking to the component's
 * own MergeOps tab, followed by its open PRs as the same flat rows used there.
 * Items arrive already filtered by the group filter bar.
 */
export const ComponentMergeOpsSection = ({
  componentPrs,
  onMerged,
  onApproved,
  selected,
  selectionDisabled,
  onSelectionChange,
  compact,
  phaseLabel,
  showNoPr,
  expanded,
  onExpandedChange,
  showApprove,
  showMerge,
  selectablePrs,
  isPrSelected,
  onPrSelectionChange,
  fetchedAtMs,
  onPrRefreshed,
}: {
  componentPrs: ComponentPrs;
  onMerged: (componentName: string, prNumber: number) => void;
  onApproved?: (componentName: string, prNumber: number) => void;
  selected?: boolean;
  selectionDisabled?: boolean;
  onSelectionChange?: (selected: boolean) => void;
  compact?: boolean;
  phaseLabel?: string;
  showNoPr?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  showApprove?: boolean;
  showMerge?: boolean;
  selectablePrs?: boolean;
  isPrSelected?: (pr: PrSummary) => boolean;
  onPrSelectionChange?: (pr: PrSummary, selected: boolean) => void;
  /** When the group list was fetched, for each row's status age. */
  fetchedAtMs?: number;
  onPrRefreshed?: (componentName: string, pr: PrSummary) => void;
}) => {
  const classes = useStyles();
  const { componentName, projectName, maintenanceMode, items, currentTags } =
    componentPrs;
  const sorted = [...items].sort((a, b) => stageRank(a) - stageRank(b));
  const canToggle = Boolean(onExpandedChange) && items.length > 0;
  const toggle = () => onExpandedChange?.(!expanded);
  const handleHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Only the header itself drives the toggle; ignore keys bubbling up from
    // child controls like the selection checkbox.
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle();
    }
  };

  return (
    <Card
      className={`${classes.card} ${compact ? classes.compactCard : ''}`}
      variant="outlined"
    >
      <CardHeader
        className={`${classes.header} ${compact ? classes.compactHeader : ''} ${
          canToggle ? classes.headerClickable : ''
        }`}
        onClick={canToggle ? toggle : undefined}
        onKeyDown={canToggle ? handleHeaderKeyDown : undefined}
        role={canToggle ? 'button' : undefined}
        tabIndex={canToggle ? 0 : undefined}
        aria-expanded={canToggle ? Boolean(expanded) : undefined}
        avatar={
          onSelectionChange ? (
            <Checkbox
              checked={Boolean(selected)}
              disabled={selectionDisabled}
              onClick={event => event.stopPropagation()}
              onChange={(_, checked) => onSelectionChange(checked)}
              inputProps={{ 'aria-label': `Select ${componentName}` }}
            />
          ) : undefined
        }
        title={
          <Box className={classes.headerTitleRow}>
            {/* The component name links to its own MergeOps detail tab; stop
                the click from bubbling so it navigates instead of toggling the
                row. */}
            <Link
              href={`/catalog/default/component/${componentName}/mergeops`}
              onClick={event => event.stopPropagation()}
              className={`${classes.headerTitle} ${
                compact ? classes.compactHeaderTitle : ''
              }`}
            >
              {componentName}
            </Link>
            {maintenanceMode && <MaintenanceModeChip />}
          </Box>
        }
        titleTypographyProps={{ variant: 'subtitle1' }}
        action={
          phaseLabel ? (
            <Box className={classes.headerActions}>
              <Chip
                label={phaseLabel}
                variant="outlined"
                size="small"
                className={classes.phaseLabel}
              />
            </Box>
          ) : undefined
        }
      />
      {sorted.length === 0 && showNoPr && (
        <Typography className={classes.noPr}>No PR</Typography>
      )}
      {sorted.map((pr, i) => (
        <Fragment key={pr.number}>
          {i > 0 && <Divider />}
          <PrRow
            pr={pr}
            componentName={componentName}
            projectName={projectName}
            currentTag={currentTagFor(pr, currentTags)}
            onMerged={n => onMerged(componentName, n)}
            onApproved={n => onApproved?.(componentName, n)}
            compact={compact}
            expanded={expanded}
            onExpandedChange={onExpandedChange}
            showApprove={showApprove}
            showMerge={showMerge}
            selectable={selectablePrs}
            selected={isPrSelected?.(pr)}
            onSelectionChange={checked => onPrSelectionChange?.(pr, checked)}
            fetchedAtMs={fetchedAtMs}
            onRefreshed={fresh => onPrRefreshed?.(componentName, fresh)}
          />
        </Fragment>
      ))}
    </Card>
  );
};
